import React, { useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import { useBookkeepingEntry } from './hooks/useBookkeepingEntry';
import BookkeepingEntryForm from './BookkeepingEntryForm';
import EntryHeader from './EntryHeader';

const FormContainer = styled.div`
  margin-bottom: 15px;
`;

const BookkeepingEditor: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const {
    entry,
    isLoading,
    isError,
    mode,
    saveEntry,
    deleteEntry,
    isSaving,
    isDeleting,
    close,
    editNextUnconfirmed,
    hasNextUnconfirmed,
  } = useBookkeepingEntry({
    entryId: id,
    isNew,
  });

  // Close editor on Escape key press
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    },
    [close]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (isLoading) {
    return (
      <ContentContainer>
        <ContentLoader />
      </ContentContainer>
    );
  }

  if (isError) {
    return (
      <ContentContainer>
        <p>{t('error_loading_data')}</p>
      </ContentContainer>
    );
  }

  return (
    <>
      <EntryHeader mode={mode} entry={entry} />
      <FormContainer>
        <BookkeepingEntryForm
          key={id} // Reset form state when entry ID changes (including new → edit or edit → new)
          entry={entry}
          mode={mode}
          isSaving={isSaving}
          isDeleting={isDeleting}
          hasNextUnconfirmed={hasNextUnconfirmed}
          onSave={saveEntry}
          onDelete={deleteEntry}
          onClose={close}
          onSaveAndNext={async (entryData) => {
            await saveEntry(entryData);
            editNextUnconfirmed();
          }}
        />
      </FormContainer>
    </>
  );
};

export default BookkeepingEditor;
